import React from 'react';
import { Insight } from '@/api/insightsApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface ResourceOverallocationVisualizationProps {
  insight: Insight;
}

export const ResourceOverallocationVisualization: React.FC<ResourceOverallocationVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  const activitiesAffected = insight.activitiesAffected || 0;
  const finishDelta = insight.finishDelta;
  const delayDays = finishDelta ? Math.abs(finishDelta) : 0;

  const allocationStatusData = [
    { status: 'Over-allocated', count: activitiesAffected, color: chartColors.danger },
    { status: 'Optimal', count: Math.floor(activitiesAffected * 1.5), color: chartColors.success },
    { status: 'Under-utilized', count: Math.floor(activitiesAffected * 0.8), color: chartColors.gray },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Over-allocated Resources</div>
          <div className="text-2xl font-bold text-red-400">{activitiesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Estimated Delay</div>
          <div className="text-2xl font-bold text-red-400">{delayDays.toFixed(1)} days</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Status</div>
          <div className="text-2xl font-bold text-amber-400">Critical</div>
        </div>
      </div>

      {/* Allocation Status */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Resource Allocation Status</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={allocationStatusData}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
            <XAxis 
              dataKey="status" 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
            />
            <YAxis 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
              label={{ value: 'Resources', angle: -90, position: 'insideLeft', style: { fill: chartTheme.textSecondary } }}
            />
            <Tooltip 
              content={<CustomTooltip formatter={(value: number) => [`${value} resources`, 'Count']} />}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {allocationStatusData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};



